import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { checklistsQueryOptions, useChecklistDeleteMutation } from "@/features/dashboard/api/queries/checklist-query";
import type { Checklist } from "@/features/dashboard/types/checklist";
import { queryClient } from "@/query-client";
import { IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

const columns: ColumnDef<Checklist>[] = [
	{
		id: "name",
		label: "Name",
		cellClassName: "min-w-[200px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<Link to={`/checklists/${data.id}`} className="flex flex-col font-semibold">
				<span className="text-blue-600">{data.name}</span>
			</Link>
		),
	},
	{
		id: "category",
		label: "Category",
		cellClassName: "min-w-[150px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{data.category}</span>,
	},
	{
		id: "project",
		label: "Project",
		cellClassName: "min-w-[150px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.project ? data.project.name : "-"}</span>,
	},
	{
		id: "description",
		label: "Description",
		cellClassName: "min-w-[200px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-600 truncate">{data.description || "-"}</span>,
	},
];

export const Route = createFileRoute("/_dashboard/checklists")({
	validateSearch: z.object({
		search: z.string().optional(),
		per_page: z.number().optional(),
		category: z.string().optional(),
		project_id: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const deleteChecklistMutation = useChecklistDeleteMutation();

	const checklistsQuery = useQuery(
		checklistsQueryOptions({
			...search,
		}),
	);

	const handleDeleteChecklist = async (checklist: Checklist) => {
		if (checklist.id) {
			await deleteChecklistMutation.mutateAsync(checklist.id.toString());
			await queryClient.invalidateQueries({
				queryKey: ["checklists"],
			});
		}
	};

	const checklists = checklistsQuery.data?.data || [];
	const meta = checklistsQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 10,
	};

	if (checklistsQuery.isLoading) {
		return (
			<Content pageName="Checklists">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading checklists...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Checklists">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Checklists</span>
						<span className="text-base text-foreground/70">Manage project checklists and reports</span>
					</div>
					<Link to="/checklists/create">
						<Button>
							<IconPlus size={18} stroke={2} />
							<span>Add New</span>
						</Button>
					</Link>
				</div>
				<div className="flex flex-col w-full gap-4">
					<DataTable
						className="border-collapse"
						data={checklists}
						columns={columns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
						onDelete={(checklist) => {
							handleDeleteChecklist(checklist);
						}}
					/>
					<Pagination
						onClick={async (page) => {
							await navigate({
								to: "/checklists",
								search: (old) => ({
									...old,
									page,
								}),
							});
						}}
						initialPage={meta.current_page}
						pageCount={meta.last_page}
					/>
				</div>
			</div>
		</Content>
	);
}
